import api from '@/lib/services/general.service'
import { evidenceBoardItems, switchThreadOptions, usedEvidence, withCarriedPick } from '@/lib/draftEvidence'

export const state = {
    emailAnalysis: null,
    isLoading: false,
    rateLimited: false,

    // The last evidence-aware reply draft (busy-ai-srv#13): `{ draft, evidence, thread, ongoing,
    // notes, fallback }` exactly as the BFF answered. One at a time — the composer holds one reply
    // — and cleared when the composer is (see `clearDraft`), so the pill and the Sources panel can
    // never describe a draft that is no longer in the box.
    replyDraft: null,
    replyDraftLoading: false,

    // The thread's own reply draft (busy-intelligent-srv): asked for the moment a conversation is
    // opened, so the answer is READY when the user reaches for Reply. Keyed by the thread it was
    // written for — a draft for one thread is never shown on another — and re-asked when the user
    // changes the tone or gives instructions.
    threadDraft: null,
    threadDraftTimelineId: null,
    threadDraftLoading: false,
    threadDraftError: null,
    // What busy-intelligent-srv's email parser found in a thread (tasks, events, contacts, notes).
    threadParse: null,
    threadParseTimelineId: null,
    threadParseLoading: false,
    threadParseError: null,
}

export const getters = {
    emailAnalysis: s => s.emailAnalysis,
    isLoading: s => s.isLoading,
    rateLimited: s => s.rateLimited,

    replyDraft: s => s.replyDraft,
    replyDraftLoading: s => s.replyDraftLoading,

    /** The draft written for `timelineId`, or null when the box holds another thread's. */
    threadDraftFor: s => (timelineId) =>
        (s.threadDraft && s.threadDraftTimelineId === String(timelineId || '')) ? s.threadDraft : null,
    threadDraftLoading: s => s.threadDraftLoading,
    threadDraftError: s => s.threadDraftError,
    /** The parser's answer for `timelineId`, or null when the store holds another thread's. */
    threadParseFor: s => (timelineId) =>
        (s.threadParse && s.threadParseTimelineId === String(timelineId || '')) ? s.threadParse : null,
    threadParseLoading: s => s.threadParseLoading,
    threadParseError: s => s.threadParseError,
    // What the drafter was GIVEN, ticked where it was used — the Sources panel's list.
    draftEvidence: s => s.replyDraft?.evidence || [],
    // The two facts the Briefing's row is written from, or null while no draft is in the box.
    draftSummary: s => s.replyDraft
        ? {
            subject: s.replyDraft.thread?.subject || '',
            timelineId: s.replyDraft.thread?.timelineId || null,
            evidence: s.replyDraft.evidence || [],
            sources: usedEvidence(s.replyDraft.evidence).length,
        }
        : null,
    /**
     * WHICH thread the reply is being written on and why (busy-main-bff#25) — its own getter rather
     * than two more keys on `draftSummary`, because it exists only when the Brain CHOSE the thread
     * (an ask, not the Reply chip) and the summary is read by surfaces that do not care which.
     */
    draftPick: s => (s.replyDraft?.pickedWhy
        ? {
            subject: s.replyDraft.thread?.subject || '',
            pickedWhy: s.replyDraft.pickedWhy,
            alternatives: s.replyDraft.alternatives || [],
        }
        : null),
    /** The other threads it could have been, ready for the "which email?" chooser. */
    draftThreadOptions: s => switchThreadOptions(s.replyDraft),
}

export const mutations = {
    SET_EMAIL_ANALYSIS(state, result) {
        state.emailAnalysis = result
    },
    SET_LOADING(state, value) {
        state.isLoading = value
    },
    CLEAR_EMAIL_ANALYSIS(state) {
        state.emailAnalysis = null
    },
    SET_RATE_LIMITED(state, value) {
        state.rateLimited = value
    },
    SET_REPLY_DRAFT(state, value) {
        state.replyDraft = value || null
    },
    SET_REPLY_DRAFT_LOADING(state, value) {
        state.replyDraftLoading = !!value
    },
    SET_THREAD_DRAFT(state, { timelineId, draft }) {
        state.threadDraft = draft || null
        state.threadDraftTimelineId = draft ? String(timelineId || '') : null
    },
    SET_THREAD_DRAFT_LOADING(state, value) {
        state.threadDraftLoading = !!value
    },
    SET_THREAD_DRAFT_ERROR(state, value) {
        state.threadDraftError = value || null
    },
    SET_THREAD_PARSE(state, { timelineId, parse }) {
        state.threadParse = parse || null
        state.threadParseTimelineId = parse ? String(timelineId || '') : null
    },
    SET_THREAD_PARSE_LOADING(state, value) {
        state.threadParseLoading = !!value
    },
    SET_THREAD_PARSE_ERROR(state, value) {
        state.threadParseError = value || null
    },
}

export const actions = {
    async analyzeEmail({ commit }, { emailBody, userPrompt }) {
        commit('SET_LOADING', true)
        try {
            const response = await api.insert('ai/analyze-email', { emailBody, userPrompt: userPrompt || null })
            if (response?.data) {
                commit('SET_EMAIL_ANALYSIS', response.data)
                return response.data
            }
        } finally {
            commit('SET_LOADING', false)
        }
        return null
    },
    clearEmailAnalysis({ commit }) {
        commit('CLEAR_EMAIL_ANALYSIS')
    },
    setRateLimited({ commit }, value) {
        commit('SET_RATE_LIMITED', value)
    },

    /**
     * "Reply to this thread using what this story knows" (busy-ai-srv#13).
     *
     * The sources go onto the Work-board as CONTEXT rather than as a selection — they are what the
     * draft read, not what the user picked — and a failure resolves to null instead of throwing:
     * the composer is already open on the thread by the time this answers, and an empty body the
     * user can type into beats an error over a composer they asked for.
     *
     * The rows survive the navigation the gesture performs because the board has been HANDED OVER to
     * the composer (`workboard/handOff`, declared by the caller BEFORE it routes — see SmartBox's
     * `draftReplyInto`): without it, the board's own rule (busy-my-web#84 — leaving the owner context
     * empties the board) wipes the sources a tick after they are written. The hand-off belongs to the
     * navigating caller rather than to this action, because it must be in place before the router
     * settles, and this action is still awaiting the service by then.
     */
    async draftReply({ commit, dispatch, state }, { timelineId = null, ongoingId = null, instruction = null } = {}) {
        // One of the two is enough since busy-main-bff#25: a thread names itself, and a story asked
        // from the box ("reply to the supplier") lets the BFF pick the thread out of its confirmed
        // mail. Neither is nothing to reply to.
        if (!timelineId && !ongoingId) return null
        commit('SET_REPLY_DRAFT_LOADING', true)
        try {
            const response = await api.insert('ai/draft-reply', {
                // Left OUT rather than sent as null when there is no thread: the field is a Guid on
                // the service, and a null would be rejected before the picker ever ran.
                ...(timelineId ? { timelineId } : {}),
                ongoingId: ongoingId || null,
                instruction: instruction || null,
                timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
            })
            // A thread the caller NAMED was not chosen by the service, so it comes back without a
            // reason or a list of alternatives — the ones the last draft carried are kept, so
            // switching thread twice is possible (busy-main-bff#25).
            const data = withCarriedPick(response?.data || null, state?.replyDraft)
            commit('SET_REPLY_DRAFT', data)
            dispatch('workboard/setEvidence', evidenceBoardItems(data?.evidence), { root: true })
            return data
        } catch {
            commit('SET_REPLY_DRAFT', null)
            return null
        } finally {
            commit('SET_REPLY_DRAFT_LOADING', false)
        }
    },

    /**
     * Ask busy-intelligent-srv (through the BFF) for a reply to the whole thread. `tone` is free
     * text from the user ("friendly", "rude", "short and firm"); `instructions` says what to say.
     * Never throws: a draft that could not be written leaves the card explaining so, with the
     * Reply pill still working exactly as before.
     */
    async draftThreadReply({ commit }, { timelineId, tone = null, instructions = null } = {}) {
        if (!timelineId) return null
        commit('SET_THREAD_DRAFT_LOADING', true)
        commit('SET_THREAD_DRAFT_ERROR', null)
        try {
            const response = await api.insertWithPath('intelligent', 'timeline/draft', {
                timelineId,
                tone: tone || null,
                instructions: instructions || null,
            })
            // The BFF answers in the ApiResultMessage envelope ({ data, isSuccessful, ... }) like
            // every other controller that returns `this.Result(...)`: the draft is `data.data`.
            const body = response?.data
            const data = (body && typeof body === 'object' && 'isSuccessful' in body) ? (body.data || null) : (body || null)
            if (!data?.draft?.body) {
                commit('SET_THREAD_DRAFT', { timelineId, draft: null })
                commit('SET_THREAD_DRAFT_ERROR', 'No reply could be drafted for this thread.')
                return null
            }
            commit('SET_THREAD_DRAFT', { timelineId, draft: data })
            return data
        } catch (error) {
            const status = error?.response?.status
            commit('SET_THREAD_DRAFT', { timelineId, draft: null })
            commit('SET_THREAD_DRAFT_ERROR', status === 503
                ? 'Reply drafting is not available in this environment.'
                : 'Could not draft a reply for this thread.')
            return null
        } finally {
            commit('SET_THREAD_DRAFT_LOADING', false)
        }
    },

    /**
     * The email parser (busy-intelligent-srv via BFF `POST v1/intelligent/timeline/parse`): the
     * tasks, events, contacts and notes it finds in the whole thread. Read-only — nothing is saved.
     * Same failure rule as the draft: null with a reason, never a throw.
     */
    async parseThread({ commit }, { timelineId } = {}) {
        if (!timelineId) return null
        commit('SET_THREAD_PARSE_LOADING', true)
        commit('SET_THREAD_PARSE_ERROR', null)
        try {
            const response = await api.insertWithPath('intelligent', 'timeline/parse', { timelineId })
            const body = response?.data
            const data = (body && typeof body === 'object' && 'isSuccessful' in body) ? (body.data || null) : (body || null)
            if (!data?.parsed) {
                commit('SET_THREAD_PARSE', { timelineId, parse: null })
                commit('SET_THREAD_PARSE_ERROR', 'Nothing could be read from this thread.')
                return null
            }
            commit('SET_THREAD_PARSE', { timelineId, parse: data })
            return data
        } catch (error) {
            const status = error?.response?.status
            commit('SET_THREAD_PARSE', { timelineId, parse: null })
            commit('SET_THREAD_PARSE_ERROR', status === 503
                ? 'The email parser is not available in this environment.'
                : status === 404
                    ? 'This thread was not found.'
                    : 'Could not read this thread right now.')
            return null
        } finally {
            commit('SET_THREAD_PARSE_LOADING', false)
        }
    },

    clearThreadDraft({ commit }) {
        commit('SET_THREAD_DRAFT', { timelineId: null, draft: null })
        commit('SET_THREAD_DRAFT_ERROR', null)
    },

    /** The composer closed or was sent — the draft and its sources go with it. */
    clearDraft({ commit, dispatch }) {
        commit('SET_REPLY_DRAFT', null)
        dispatch('workboard/setEvidence', [], { root: true })
    },
}
